import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FiUsers, FiAlertCircle, FiCalendar, FiDollarSign, FiClock } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { convertAmount, formatAmount, getExchangeRates } from '../../services/exchangeRates';

function localToday() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

export default function DashboardOverview({ clients }) {
  const [currency, setCurrency] = useState('LKR');
  const [rates, setRates] = useState(null);
  const [rateError, setRateError] = useState('');
  const today = localToday();
  const active = clients.filter(client => client.is_active && !client.is_archived);
  const due = active.filter(client => client.next_due_date <= today);
  const upcoming = active.filter(client => client.next_due_date > today);
  const archived = clients.filter(c => c.is_archived);
  const monthlyClients = active.filter(c => c.billing_frequency === 'monthly');
  const missingCurrency = monthlyClients.some(client =>
    convertAmount(client.monthly_amount, client.currency, currency, rates) === null
  );
  const monthly = missingCurrency ? null : monthlyClients.reduce((total, client) =>
    total + convertAmount(client.monthly_amount, client.currency, currency, rates), 0
  );

  useEffect(() => {
    let activeRequest = true;
    getExchangeRates()
      .then(result => {
        if (activeRequest) {
          setRates(result);
          setRateError('');
        }
      })
      .catch(() => {
        if (activeRequest) setRateError('Live exchange rates are unavailable. Converted totals cannot be shown.');
      });
    return () => { activeRequest = false; };
  }, []);

  const cards = [
    {
      label: 'Active Clients',
      value: active.length,
      detail: `${clients.length - archived.length} total (${archived.length} archived)`,
      icon: FiUsers,
      gradient: 'from-blue-500 to-blue-600',
      bgLight: 'bg-blue-50',
      textColor: 'text-blue-600'
    },
    {
      label: 'Due Now',
      value: due.length,
      detail: 'Needs attention',
      icon: FiAlertCircle,
      gradient: 'from-amber-500 to-orange-500',
      bgLight: 'bg-amber-50',
      textColor: 'text-amber-600'
    },
    {
      label: 'Upcoming',
      value: upcoming.length,
      detail: 'Future payments',
      icon: FiCalendar,
      gradient: 'from-indigo-500 to-purple-500',
      bgLight: 'bg-indigo-50',
      textColor: 'text-indigo-600'
    },
    {
      label: 'Monthly Revenue',
      value: monthly === null ? '—' : formatAmount(monthly, currency),
      detail: monthly === null ? 'Waiting for exchange rates' : `Estimated in ${currency} · monthly clients only`,
      icon: FiDollarSign,
      gradient: 'from-emerald-500 to-teal-500',
      bgLight: 'bg-emerald-50',
      textColor: 'text-emerald-600'
    }
  ];

  const freqBreakdown = active.reduce((acc, c) => {
    const key = c.billing_frequency || 'monthly';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const freqLabels = { monthly: 'Monthly', quarterly: 'Quarterly', semi_annual: '6-Month', annual: 'Annual' };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="mb-4 flex flex-wrap items-center justify-end gap-3">
        <label htmlFor="dashboard-currency" className="text-sm font-medium text-slate-600">Show totals in</label>
        <select
          id="dashboard-currency"
          value={currency}
          onChange={event => setCurrency(event.target.value)}
          className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
        >
          <option value="LKR">LKR · Sri Lankan rupee</option>
          <option value="SGD">SGD · Singapore dollar</option>
        </select>
      </div>
      {rateError && <p className="mb-4 text-sm text-amber-700" role="status">{rateError}</p>}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map((card, index) => {
          const Icon = card.icon;
          return (
            <motion.div
              key={card.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all hover:shadow-md hover:border-slate-300"
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${card.gradient} shadow-lg`}>
                  <SafeIcon icon={Icon} className="w-5 h-5 text-white" />
                </div>
                <span className={`text-xs font-bold uppercase tracking-wider ${card.textColor}`}>
                  {card.label}
                </span>
              </div>
              <p className="text-3xl font-bold tracking-tight text-slate-900">{card.value}</p>
              <p className="mt-1 text-sm text-slate-500">{card.detail}</p>
              <div className={`absolute -right-4 -bottom-4 h-24 w-24 rounded-full ${card.bgLight} opacity-50 group-hover:opacity-75 transition-opacity`} />
            </motion.div>
          );
        })}
      </div>

      {due.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-6 flex items-center gap-4 rounded-2xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 px-6 py-4"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100">
            <SafeIcon icon={FiClock} className="w-5 h-5 text-amber-600" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-amber-900">
              {due.length} client{due.length === 1 ? '' : 's'} due for payment
            </p>
            <p className="text-sm text-amber-700">Review the customer list below to take action.</p>
          </div>
        </motion.div>
      )}

      {active.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-4 flex flex-wrap items-center gap-3 text-sm text-slate-500"
        >
          <span className="font-medium text-slate-600">Billing breakdown:</span>
          {Object.entries(freqBreakdown).map(([freq, count]) => (
            <span key={freq} className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
              {freqLabels[freq] || freq}: {count}
            </span>
          ))}
        </motion.div>
      )}

      <p className="mt-4 text-xs text-slate-400">
        Monthly revenue shows monthly-frequency clients only; other frequencies are billed at their own cadence.
      </p>
    </motion.div>
  );
}